import React, { useState, useRef } from 'react';
import { Card, CardHeader, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ProcessingOverlay } from '../ui/loading';
import { ProjectType } from '../../models/sow';
import {
  ChefHat, Bath, Home, Building, Wrench, ArrowRight, CheckCircle,
  Loader2, Lightbulb, Camera, Upload, X, Sparkles
} from 'lucide-react';

type InputMode = 'select' | 'describe' | 'photos';

interface PhotoUpload {
  id: string;
  file: File;
  preview: string;
  state: 'pending' | 'uploading' | 'analyzing' | 'done' | 'error';
  imageId?: string;
  caption?: string;
  roomType?: string;
  error?: string;
}

interface ScopeGatheringStepProps {
  onProjectTypeSelected: (projectType: ProjectType) => void;
  onDescriptionProvided: (description: string) => Promise<void>;
  onGenerateScope?: (imageIds: string[]) => Promise<void>;
  onImageUpload?: (file: File) => Promise<{ imageId: string }>;
  existingImages?: Array<{ imageId: string; url?: string; description?: string; roomType?: string; status?: string }>;
  address: string;
}

const projectTypeOptions = [
  { value: 'Kitchen' as ProjectType, icon: ChefHat, description: 'Kitchen remodel or renovation' },
  { value: 'Bathroom' as ProjectType, icon: Bath, description: 'Bathroom renovation or updates' },
  { value: 'Full House' as ProjectType, icon: Home, description: 'Whole home renovation' },
  { value: 'Basement' as ProjectType, icon: Building, description: 'Basement finishing or remodel' },
  { value: 'Custom' as ProjectType, icon: Wrench, description: 'Other renovation type' }
];

export const ScopeGatheringStep: React.FC<ScopeGatheringStepProps> = ({
  onProjectTypeSelected,
  onDescriptionProvided,
  onGenerateScope,
  onImageUpload,
  existingImages,
  address,
}) => {
  const [mode, setMode] = useState<InputMode>(existingImages && existingImages.length > 0 ? 'photos' : 'describe');
  const [selectedProjectType, setSelectedProjectType] = useState<ProjectType | ''>('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  // Photo upload state — initialize from existing analyzed images
  const [photos, setPhotos] = useState<PhotoUpload[]>(() => {
    if (!existingImages || existingImages.length === 0) return [];
    return existingImages
      .filter(img => img.status === 'analyzed' && img.imageId)
      .map(img => ({
        id: img.imageId,
        file: null as any, // no file object for restored images
        preview: img.url || '',
        state: 'done' as const,
        imageId: img.imageId,
        caption: img.description || '',
        roomType: img.roomType || '',
      }));
  });
  const [generatingScope, setGeneratingScope] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleContinueWithSelection = () => {
    if (selectedProjectType) {
      onProjectTypeSelected(selectedProjectType);
    }
  };

  const handleContinueWithDescription = async () => {
    if (description.trim()) {
      setLoading(true);
      try {
        await onDescriptionProvided(description.trim());
      } catch (error) {
        console.error('Error analyzing description:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Photo handlers
  const handleFilesSelected = async (files: FileList | null) => {
    if (!files || files.length === 0 || !onImageUpload) return;

    const newPhotos: PhotoUpload[] = Array.from(files).map(file => ({
      id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
      state: 'pending' as const
    }));

    // Show all thumbnails immediately as uploading
    const withUploading = newPhotos.map(p => ({ ...p, state: 'uploading' as const }));
    setPhotos(prev => [...prev, ...withUploading]);

    // Upload all in parallel
    await Promise.all(newPhotos.map(async (photo) => {
      try {
        const { imageId } = await onImageUpload(photo.file);
        setPhotos(prev => prev.map(p =>
          p.id === photo.id ? { ...p, state: 'done' as const, imageId } : p
        ));
      } catch (err) {
        setPhotos(prev => prev.map(p =>
          p.id === photo.id ? { ...p, state: 'error' as const, error: 'Upload failed' } : p
        ));
      }
    }));
  };

  const handleRemovePhoto = (photoId: string) => {
    setPhotos(prev => {
      const photo = prev.find(p => p.id === photoId);
      if (photo?.preview) URL.revokeObjectURL(photo.preview);
      return prev.filter(p => p.id !== photoId);
    });
  };

  const handleGenerateScope = async () => {
    if (!onGenerateScope) return;
    const ids = photos.filter(p => p.imageId).map(p => p.imageId!);
    if (ids.length === 0) return;
    setGeneratingScope(true);
    try {
      await onGenerateScope(ids);
    } catch (error) {
      console.error('Error generating scope:', error);
    } finally {
      setGeneratingScope(false);
    }
  };

  const doneCount = photos.filter(p => p.state === 'done').length;
  const pendingOrUploading = photos.filter(p => p.state === 'pending' || p.state === 'uploading' || p.state === 'analyzing').length;
  const canGenerateScope = photos.length > 0 && doneCount === photos.length && pendingOrUploading === 0;

  const canContinue = mode === 'select' ? !!selectedProjectType : mode === 'describe' ? description.trim().length > 0 : false;

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-2xl">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-2">Define Your Project Scope</h1>
        <p className="text-sm md:text-base text-gray-600">
          Tell us about the renovation you're planning at <strong>{address}</strong>
        </p>
      </div>

      <Card className="border-0 shadow-none">
        <CardHeader className="px-4 md:px-6">
          {/* Mode Toggle */}
          <div className="flex gap-2">
            <Button
              variant={mode === 'photos' ? 'default' : 'outline'}
              onClick={() => setMode('photos')}
              className="flex-1 text-sm md:text-base py-2 md:py-3"
            >
              <Camera className="h-4 w-4 mr-1.5" />
              <span className="hidden sm:inline">Start with Photos</span>
              <span className="sm:hidden">Photos</span>
            </Button>
            <Button
              variant={mode === 'describe' ? 'default' : 'outline'}
              onClick={() => setMode('describe')}
              className="flex-1 text-sm md:text-base py-2 md:py-3"
            >
              <span className="hidden sm:inline">Describe Project</span>
              <span className="sm:hidden">Describe</span>
            </Button>
            <Button
              variant={mode === 'select' ? 'default' : 'outline'}
              onClick={() => setMode('select')}
              className="flex-1 text-sm md:text-base py-2 md:py-3"
            >
              Quick Select
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-4 px-4 md:px-6">
          {/* PHOTOS MODE */}
          {mode === 'photos' && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                <div className="flex items-start gap-2">
                  <Camera className="h-4 w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs md:text-sm text-blue-900">
                    <p className="font-medium mb-1">Upload property photos</p>
                    <p className="text-blue-700">
                      Drop in listing photos or snapshots of each room. We'll analyze them and recommend a renovation scope.
                    </p>
                  </div>
                </div>
              </div>

              {/* Drop zone */}
              <div
                className="border-2 border-dashed border-blue-300 rounded-lg p-6 md:p-8 text-center hover:border-blue-400 transition-colors bg-blue-50/30"
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleFilesSelected(e.dataTransfer.files);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    handleFilesSelected(e.target.files);
                    e.target.value = '';
                  }}
                  className="hidden"
                  id="scope-photo-input"
                />
                <label htmlFor="scope-photo-input" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto text-blue-500 mb-3" />
                  <div className="text-sm font-medium text-blue-900 mb-1">
                    Click to select photos or drag & drop
                  </div>
                  <div className="text-xs text-blue-600">
                    JPG, PNG — upload as many as you'd like
                  </div>
                </label>
              </div>

              {/* Photo grid with real-time captions */}
              {photos.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      {doneCount} of {photos.length} photo{photos.length !== 1 ? 's' : ''} uploaded
                    </span>
                    {pendingOrUploading > 0 && (
                      <span className="text-xs text-blue-600 flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Processing...
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photos.map(photo => (
                      <div key={photo.id} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                          <img
                            src={photo.preview}
                            alt={photo.file?.name || 'Photo'}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        {/* State overlays */}
                        {(photo.state === 'uploading' || photo.state === 'analyzing') && (
                          <div className="absolute inset-0 bg-white/70 rounded-lg flex items-center justify-center">
                            <div className="text-center">
                              <Loader2 className="w-5 h-5 animate-spin mx-auto text-blue-600 mb-1" />
                              <span className="text-xs text-blue-700">
                                {photo.state === 'uploading' ? 'Uploading...' : 'Analyzing...'}
                              </span>
                            </div>
                          </div>
                        )}

                        {photo.state === 'error' && (
                          <div className="absolute inset-0 bg-red-50/80 rounded-lg flex items-center justify-center">
                            <span className="text-xs text-red-600 font-medium">{photo.error}</span>
                          </div>
                        )}

                        {/* Remove button */}
                        <button
                          onClick={() => handleRemovePhoto(photo.id)}
                          className="absolute top-1 right-1 bg-white/90 hover:bg-white text-gray-600 hover:text-red-600 rounded-full p-1 shadow-sm border border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>

                        {/* Caption */}
                        {photo.state === 'done' && photo.caption && (
                          <div className="mt-1.5 px-0.5">
                            <div className="text-xs font-medium text-gray-800 capitalize">
                              {photo.roomType?.replace(/_/g, ' ') || 'Room'}
                            </div>
                            <div className="text-xs text-gray-500 line-clamp-2">{photo.caption}</div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Generate Scope button */}
                  <Button
                    onClick={handleGenerateScope}
                    disabled={!canGenerateScope || generatingScope}
                    className="w-full"
                    size="lg"
                  >
                    {generatingScope ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating scope...
                      </>
                    ) : pendingOrUploading > 0 ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Analyzing photos...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Scope ({doneCount} photo{doneCount !== 1 ? 's' : ''})
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* DESCRIBE MODE */}
          {mode === 'describe' && (
            <div className="space-y-3 md:space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                <div className="flex items-start gap-2">
                  <Lightbulb className="h-4 w-4 md:h-5 md:w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs md:text-sm text-blue-900">
                    <p className="font-medium mb-1">Tell us about your project</p>
                    <p className="text-blue-700">
                      Examples: "I want to remodel my kitchen with new cabinets and countertops" or
                      "Looking to update my master bathroom, replace shower and vanity"
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Project Description *
                </label>
                <textarea
                  className="w-full px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  rows={5}
                  placeholder="Describe what you'd like to renovate... Include the rooms, specific changes you want, and any priorities you have."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={loading}
                />
                <p className="text-xs md:text-sm text-gray-500 mt-1">
                  {description.length} characters
                </p>
              </div>
            </div>
          )}

          {/* SELECT MODE */}
          {mode === 'select' && (
            <div className="grid gap-2 md:gap-3">
              {projectTypeOptions.map((option) => {
                const IconComponent = option.icon;
                const isSelected = selectedProjectType === option.value;

                return (
                  <div
                    key={option.value}
                    onClick={() => setSelectedProjectType(option.value)}
                    className={`p-3 md:p-4 border rounded-lg cursor-pointer transition-colors ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <IconComponent className={`h-5 w-5 md:h-6 md:w-6 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                      </div>
                      <div className="flex-grow min-w-0">
                        <h3 className="font-medium text-sm md:text-base">{option.value}</h3>
                        <p className="text-xs md:text-sm text-gray-600">{option.description}</p>
                      </div>
                      {isSelected && (
                        <div className="flex-shrink-0">
                          <CheckCircle className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Continue buttons for describe/select modes */}
          {canContinue && (
            <div className="grid grid-cols-2 gap-2 md:gap-3 mt-4 md:mt-6 pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => mode === 'select' ? setSelectedProjectType('') : setDescription('')}
                disabled={loading}
                className="text-sm md:text-base"
              >
                Clear
              </Button>
              <Button
                onClick={mode === 'select' ? handleContinueWithSelection : handleContinueWithDescription}
                disabled={loading}
                className="text-sm md:text-base"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-1 md:mr-2 h-3 w-3 md:h-4 md:w-4 animate-spin" />
                    <span className="hidden sm:inline">Analyzing...</span>
                    <span className="sm:hidden">Wait...</span>
                  </>
                ) : (
                  <>
                    Continue
                    <ArrowRight className="ml-1 md:ml-2 h-3 w-3 md:h-4 md:w-4" />
                  </>
                )}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Processing overlay for scope generation */}
      <ProcessingOverlay
        visible={generatingScope}
        message="Analyzing your property"
        detail="This typically takes 30-60 seconds"
        steps={[
          { label: 'Grouping photos into rooms', complete: false, active: true },
          { label: 'Identifying improvements needed', complete: false, active: false },
          { label: 'Generating work items', complete: false, active: false },
          { label: 'Estimating costs', complete: false, active: false },
        ]}
      />
    </div>
  );
};
